/**
 * Authentication Modal Controller
 * Handles modal opening, closing, view switching, and Shopify form submissions
 */

class AuthModal {
  constructor() {
    this.modal = document.getElementById('auth-modal');
    this.currentView = 'login';
    this.views = ['login', 'register', 'forgot-password'];
    this.observer = null; // Store observer reference for cleanup
    this.init();
  }

  init() {
    if (!this.modal) return;

    // Critical: Setup Open/Close listeners immediately for perceived responsiveness
    this.setupEventListeners();

    // Defer Non-Critical Setup to Idle Time (Performance)
    const setupNonCritical = () => {
      // Handle password toggles
      this.setupPasswordToggles();

      // Handle error dismissal
      this.setupErrorDismissal();

      // Setup client-side validation
      this.setupLoginValidation();

      // Monitor Register Form Submission
      this.setupRegisterMonitoring();

      // Restore State (Errors/Flags) - Priority check
      this.restoreState();

      // Handle URL hash changes
      this.handleHashChange();

      // Initialize image text (only if not already opened by active state)
      if (!this.isOpen()) {
        this.updateImageText('login');
      }
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => setupNonCritical(), { timeout: 2000 });
    } else {
      setTimeout(setupNonCritical, 50);
    }

    // Expose global method to open modal
    window.openAuthModal = (view = 'login') => this.open(view);
    window.closeAuthModal = () => this.close();
  }

  setupEventListeners() {
    // Close buttons
    const closeButtons = this.modal.querySelectorAll('[data-auth-close]');
    closeButtons.forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });

    // Back buttons
    const backButtons = this.modal.querySelectorAll('[data-auth-back]');
    backButtons.forEach(btn => {
      btn.addEventListener('click', () => this.goBack());
    });

    // View switchers
    const switchers = this.modal.querySelectorAll('[data-auth-switch]');
    switchers.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const view = btn.getAttribute('data-auth-switch');
        this.switchView(view);
      });
    });

    // Close on backdrop click
    const backdrop = this.modal.querySelector('.auth-modal__backdrop');
    if (backdrop) {
      backdrop.addEventListener('click', () => this.close());
    }

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });

    // Prevent body scroll when modal is open - store observer for potential cleanup
    this.observer = new MutationObserver(() => {
      if (this.isOpen()) {
        document.body.classList.add('auth-modal-open');
      } else {
        document.body.classList.remove('auth-modal-open');
      }
    });

    this.observer.observe(this.modal, { attributes: true, attributeFilter: ['aria-hidden'] });
  }

  // Cleanup method for proper resource management
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    window.openAuthModal = undefined;
    window.closeAuthModal = undefined;
  }

  setupPasswordToggles() {
    const toggles = this.modal.querySelectorAll('[data-password-toggle]');
    toggles.forEach(toggle => {
      toggle.addEventListener('click', () => {
        const wrapper = toggle.closest('.auth-form__password-wrapper');
        const input = wrapper.querySelector('input[type="password"], input[type="text"]');
        const hiddenIcon = wrapper.querySelector('.auth-form__password-icon--hidden');
        const visibleIcon = wrapper.querySelector('.auth-form__password-icon--visible');

        if (input.type === 'password') {
          input.type = 'text';
          if (hiddenIcon) hiddenIcon.style.display = 'none';
          if (visibleIcon) visibleIcon.style.display = 'block';
        } else {
          input.type = 'password';
          if (hiddenIcon) hiddenIcon.style.display = 'block';
          if (visibleIcon) visibleIcon.style.display = 'none';
        }
      });
    });
  }

  setupErrorDismissal() {
    // Handle close buttons on errors
    this.modal.addEventListener('click', (e) => {
      const closeBtn = e.target.closest('.auth-form__error-close');
      if (closeBtn) {
        e.preventDefault();
        const errorAlert = closeBtn.closest('.auth-form__error');
        if (errorAlert) {
          errorAlert.remove();
        }
      }
    });
  }



  setupLoginValidation() {
    // Intercept login submissions for client-side validation ONLY
    this.modal.addEventListener('submit', (e) => {
      const form = e.target;

      // Strict check: Must be the login form
      if (form.id !== 'customer_login_form' || !form.getAttribute('action')?.includes('/account/login')) {
        return;
      }

      const loginForm = form;
      const emailInput = loginForm.querySelector('input[name="customer[email]"]');

      // Email Validation Regex
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (emailInput && !emailRegex.test(emailInput.value)) {
        // INVALID EMAIL: Stop everything and show error
        e.preventDefault();
        e.stopPropagation();

        this.showFormError(loginForm, 'Please enter a valid email address');
        return;
      }

      // If email is valid -> Use Instant Feedback Handler
      // We do NOT prevent default here, but we inject UI updates immediately
      this.handleFormSubmitEffect(form);

    }, true); // Capture phase
  }

  setupRegisterMonitoring() {
    // Monitor Create Account form to flag submission attempt
    const registerForm = this.modal.querySelector('#create_customer_form');
    if (registerForm) {
      registerForm.addEventListener('submit', (e) => {
        // Set flag to detect reload state
        sessionStorage.setItem('auth_signup_attempt', 'true');
        // Immediate UI Feedback
        this.handleFormSubmitEffect(registerForm);
      }, true);
    }

    // Also monitor recover form
    const recoverForm = this.modal.querySelector('#recover_customer_password_form');
    if (recoverForm) {
      recoverForm.addEventListener('submit', (e) => {
        this.handleFormSubmitEffect(recoverForm);
      }, true);
    }
  }

  // New Method: Provides instant visual feedback of action
  handleFormSubmitEffect(form) {
    const submitBtn = form.querySelector('button[type="submit"]');
    if (!submitBtn) return;

    // Prevent double clicks
    if (submitBtn.disabled) return;

    // Save original text
    const originalText = submitBtn.innerText;

    // Spinner SVG
    const spinner = `<svg class="auth-spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><style>.auth-spinner{animation:spin 1s linear infinite}@keyframes spin{100%{transform:rotate(360deg)}}</style><path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z" fill="currentColor" fill-opacity="0.3"/><path d="M12 4V2C6.48 2 2 6.48 2 12H4C4 7.59 7.59 4 12 4Z" fill="currentColor"/></svg>`;

    // UI Updates
    submitBtn.disabled = true;
    submitBtn.style.opacity = '0.8';
    submitBtn.style.cursor = 'not-allowed';
    submitBtn.innerHTML = `${spinner}`; // Replace text with spinner

    // Note: We don't need to re-enable because the page will reload/redirect on success
    // If it fails (Shopify rendering same page), the page reloads anyway with errors

    // SAFETY: If the page doesn't unload in 8 seconds (very slow connection), reset button
    setTimeout(() => {
      submitBtn.disabled = false;
      submitBtn.style.opacity = '1';
      submitBtn.style.cursor = 'pointer';
      submitBtn.innerText = originalText;
    }, 8000);
  }

  restoreState() {
    // 1. Check Signup Attempt Flag (Priority)
    const isSignupAttempt = sessionStorage.getItem('auth_signup_attempt') === 'true';
    const registerForm = this.modal.querySelector('#create_customer_form');

    if (isSignupAttempt) {
      // Clean up flag immediately so it doesn't persist inappropriately
      sessionStorage.removeItem('auth_signup_attempt');

      if (registerForm) {
        // Only open if there is actual content in the error/success containers
        const hasError = registerForm.querySelector('.auth-form__error');
        const hasSuccess = registerForm.querySelector('.auth-form__success');

        if ((hasError && hasError.innerText.trim().length > 0) || hasSuccess) {
          this.open('register');
          return;
        }
      }
    }

    // 2. Standard Error/Success Checking (Fallback)
    // Only open if we detect VISIBLE errors with actual text content
    const loginForm = this.modal.querySelector('#customer_login_form');
    const forgotForm = this.modal.querySelector('#recover_customer_password_form');

    const hasLoginError = loginForm && loginForm.querySelector('.auth-form__error') && loginForm.querySelector('.auth-form__error').innerText.trim().length > 0;
    const hasRegisterError = registerForm && registerForm.querySelector('.auth-form__error') && registerForm.querySelector('.auth-form__error').innerText.trim().length > 0;
    const hasRegisterSuccess = registerForm && registerForm.querySelector('.auth-form__success');
    const hasForgotError = forgotForm && forgotForm.querySelector('.auth-form__error') && forgotForm.querySelector('.auth-form__error').innerText.trim().length > 0;
    const hasForgotSuccess = forgotForm && forgotForm.querySelector('.auth-form__success');

    if (hasLoginError) {
      this.open('login');
    } else if (hasRegisterError || hasRegisterSuccess) {
      this.open('register');
    } else if (hasForgotError || hasForgotSuccess) {
      this.open('forgot-password');
    }

    // 3. Check SessionStorage redirect (legacy)
    // Runs only if no errors were found above
    const modalToOpen = sessionStorage.getItem('openAuthModal');
    if (modalToOpen && this.views.includes(modalToOpen)) {
      sessionStorage.removeItem('openAuthModal');
      // No timeout needed - open immediately if valid
      this.open(modalToOpen);
    }
  }

  showFormError(form, message, isHtml = false) {
    const existingError = form.querySelector('.auth-form__error');
    if (existingError) existingError.remove();

    const currentErrorDiv = document.createElement('div');
    currentErrorDiv.className = 'auth-form__error';
    currentErrorDiv.setAttribute('tabindex', '-1');

    const errorIcon = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>`;

    const contentArgs = isHtml ? `<ul class="auth-form__error-list">${message}</ul>` : `<ul class="auth-form__error-list"><li>${message}</li></ul>`;

    currentErrorDiv.innerHTML = `
        <span class="auth-form__error-icon">${errorIcon}</span>
        <div class="auth-form__error-content">
            <strong class="auth-form__error-title">Please adjust the following:</strong>
            ${contentArgs}
        </div>
        <button type="button" class="auth-form__error-close" aria-label="Dismiss error">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 1L1 13M1 1l12 12"></path></svg>
        </button>
     `;

    // Dismiss listener
    currentErrorDiv.querySelector('.auth-form__error-close').addEventListener('click', () => currentErrorDiv.remove());

    const firstField = form.querySelector('.auth-form__field');
    if (firstField) {
      form.insertBefore(currentErrorDiv, firstField);
    } else {
      form.prepend(currentErrorDiv);
    }

    currentErrorDiv.focus();
  }

  handleHashChange() {
    // Check initial hash
    if (window.location.hash) {
      const view = window.location.hash.substring(1);
      if (this.views.includes(view)) {
        this.open(view);
        history.replaceState(null, null, window.location.pathname);
      }
    }

    // Listen for hash changes
    window.addEventListener('hashchange', () => {
      const hash = window.location.hash.substring(1);
      if (this.views.includes(hash)) {
        this.open(hash);
        history.replaceState(null, null, window.location.pathname);
      }
    });
  }

  open(view = 'login') {
    if (!this.modal) return;

    // Remove FOUC prevention style
    this.modal.style.display = 'flex';

    // Validate view
    if (!this.views.includes(view)) {
      view = 'login';
    }

    // Hide all views first
    const allViews = this.modal.querySelectorAll('.auth-view');
    allViews.forEach(v => v.classList.remove('active'));

    // Show the requested view
    const targetView = this.modal.querySelector(`.auth-view[data-auth-view="${view}"]`);
    if (targetView) {
      targetView.classList.add('active');
    }

    this.currentView = view;

    // Update image side text
    this.updateImageText(view);

    this.modal.setAttribute('aria-hidden', 'false');

    // Focus first input (Desktop only to prevent keyboard popup on mobile)
    setTimeout(() => {
      if (targetView && window.innerWidth > 989) {
        const firstInput = targetView.querySelector('.auth-form__input');
        if (firstInput) {
          firstInput.focus();
        }
      }
    }, 300);
  }

  close() {
    if (!this.modal) return;

    this.modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('auth-modal-open');

    // Reset to login view for next time
    const allViews = this.modal.querySelectorAll('.auth-view');
    allViews.forEach(v => v.classList.remove('active'));
    const loginView = this.modal.querySelector('.auth-view--login');
    if (loginView) {
      loginView.classList.add('active');
      this.currentView = 'login';
      this.updateImageText('login');
    }
  }

  switchView(view) {
    if (!this.views.includes(view)) return;

    const currentViewElement = this.modal.querySelector(`.auth-view[data-auth-view="${this.currentView}"]`);
    const newViewElement = this.modal.querySelector(`.auth-view[data-auth-view="${view}"]`);

    if (!newViewElement) return;

    // Hide current view
    if (currentViewElement) {
      currentViewElement.classList.remove('active');
    }

    // Show new view
    newViewElement.classList.add('active');
    this.currentView = view;

    // Clear any errors in the new view (fresh start)
    const errors = newViewElement.querySelectorAll('.auth-form__error');
    errors.forEach(err => err.remove());

    // Update image side text based on view
    this.updateImageText(view);

    // Focus first input after animation (Desktop only)
    setTimeout(() => {
      if (window.innerWidth > 989) {
        const firstInput = newViewElement.querySelector('.auth-form__input');
        if (firstInput) {
          firstInput.focus();
        }
      }
    }, 200);
  }

  updateImageText(view) {
    const titleLogin = this.modal.querySelector('[data-image-title-login]');
    const titleRegister = this.modal.querySelector('[data-image-title-register]');
    const titleForgot = this.modal.querySelector('[data-image-title-forgot]');
    const subtitleLogin = this.modal.querySelector('[data-image-subtitle-login]');
    const subtitleRegister = this.modal.querySelector('[data-image-subtitle-register]');
    const subtitleForgot = this.modal.querySelector('[data-image-subtitle-forgot]');

    // IMAGE TOGGLE LOGIC
    const imageWrapper = this.modal.querySelector('.auth-modal__image-wrapper');
    if (imageWrapper) {
      imageWrapper.setAttribute('data-view', view);
    }

    // Hide all first
    if (titleLogin) titleLogin.style.display = 'none';
    if (titleRegister) titleRegister.style.display = 'none';
    if (titleForgot) titleForgot.style.display = 'none';
    if (subtitleLogin) subtitleLogin.style.display = 'none';
    if (subtitleRegister) subtitleRegister.style.display = 'none';
    if (subtitleForgot) subtitleForgot.style.display = 'none';

    // Show appropriate ones
    if (view === 'register') {
      if (titleRegister) titleRegister.style.display = 'block';
      if (subtitleRegister) subtitleRegister.style.display = 'block';
    } else if (view === 'forgot-password') {
      if (titleForgot) titleForgot.style.display = 'block';
      if (subtitleForgot) subtitleForgot.style.display = 'block';
    } else {
      if (titleLogin) titleLogin.style.display = 'block';
      if (subtitleLogin) subtitleLogin.style.display = 'block';
    }
  }

  goBack() {
    // Determine previous view based on current view
    const backMap = {
      'register': 'login',
      'forgot-password': 'login'
    };

    const previousView = backMap[this.currentView] || 'login';
    this.switchView(previousView);
  }

  isOpen() {
    return this.modal && this.modal.getAttribute('aria-hidden') === 'false';
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new AuthModal();
  });
} else {
  new AuthModal();
}


// Handle login/register links throughout the site
document.addEventListener('click', (e) => {
  const link = e.target.closest('a');
  if (!link) return;

  const href = link.getAttribute('href');

  // Check for data attribute first (explicit trigger)
  if (link.hasAttribute('data-auth-trigger')) {
    e.preventDefault();
    const view = link.getAttribute('data-auth-trigger') || 'login';
    if (window.openAuthModal) {
      window.openAuthModal(view);
    }
    return;
  }

  // Check if it's a login/register link - ALWAYS intercept these
  if (href && (href.includes('/account/login') || href.includes('/account/register'))) {
    e.preventDefault();
    e.stopPropagation();

    // Determine which view to open
    let view = 'login';
    if (href.includes('/account/register') ||
      link.textContent.toLowerCase().includes('register') ||
      link.textContent.toLowerCase().includes('sign up') ||
      link.textContent.toLowerCase().includes('create account')) {
      view = 'register';
    }

    if (window.openAuthModal) {
      window.openAuthModal(view);
    }
    return false;
  }
});
